import React from 'react';
import { Box, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	ItemPricingWithoutCurrency,
	ItemPricing,
	OrderPricing,
} from 'common/modules/atoms/pricing';
import { PricingRow } from 'common/components/molecules/PricingRow';
import { CurrencyObject, EnumKeys, Languages } from 'common/types';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { TFunction } from 'i18next';
import { PaymentAmounts } from 'common/modules/atoms/paymentAmounts';
import { stringEnum } from 'common/utils/objects';
import { getCombinedTaxLines, getDefaultTaxLabel } from 'common/modules/atoms/taxes';
import { getTranslation } from 'common/modules/translations';

const PriceRows = stringEnum([
	'manualDiscount',
	'discountCodes',
	'subtotal',
	'taxes',
	'total',
	'deposit',
	'paid',
	'includedTax',
]);

type PriceRow = EnumKeys<typeof PriceRows>;

export const defaultPriceRows: PriceRow[] = [
	'manualDiscount',
	'discountCodes',
	'subtotal',
	'taxes',
	'total',
	'deposit',
];
interface Props {
	pricing: OrderPricing | ItemPricing | ItemPricingWithoutCurrency;
	shopCurrency: CurrencyObject;
	payment?: PaymentAmounts;
	styling?: {
		totalDivider?: boolean;
		fontSize?: 'normal' | 'large';
		verticalLinePadding?: number;
		textRowClass?: string;
		totalRowClass?: string;
		totalRowSpacing?: number;
		showCombinedTaxLabels?: boolean;
	};
	rows?: PriceRow[];
	t: TFunction;
	lang: Languages;
}

const getManualDiscount = (
	pricing: OrderPricing | ItemPricing | ItemPricingWithoutCurrency,
): number => {
	if (typeof pricing.manualDiscount === 'number') {
		return pricing.manualDiscount;
	}
	return pricing.manualDiscount?.amount || 0;
};

const getDeposit = (pricing: OrderPricing | ItemPricing | ItemPricingWithoutCurrency): number => {
	if (typeof pricing.deposit === 'number') {
		return pricing.deposit;
	}
	return pricing.deposit?.value || 0;
};

export const PriceBreakdown = (props: Props) => {
	const classes = useStyles(props);
	const { pricing, shopCurrency, t, styling = {}, payment, rows = defaultPriceRows, lang } = props;
	const {
		fontSize = 'normal',
		textRowClass,
		totalRowClass,
		showCombinedTaxLabels = true,
	} = styling;
	const manualDiscount = getManualDiscount(pricing);
	const deposit = getDeposit(pricing);
	const { total, subtotal, taxLines: _taxLines, discountCodes, taxExcluded } = pricing;
	const currency = pricing.currency
		? getCurrencyObjectFromCurrencyCode(pricing.currency, shopCurrency)
		: undefined;
	const verticalLinePadding = styling?.verticalLinePadding ?? 0;
	const taxLines = showCombinedTaxLabels ? getCombinedTaxLines(_taxLines) : _taxLines;
	return (
		<Box>
			<Box className={rows.includes(PriceRows.total) ? classes.subtotalWrapper : undefined}>
				{!!manualDiscount && rows.includes(PriceRows.manualDiscount) && (
					<PricingRow
						label={t('common:discount', 'Discount')}
						value={-manualDiscount}
						currency={currency}
						py={verticalLinePadding}
						variant={fontSize === 'large' ? 'body1' : 'body2'}
						textClass={textRowClass}
					/>
				)}
				{!!discountCodes &&
					rows.includes(PriceRows.discountCodes) &&
					Object.entries(discountCodes).map(([code, value]) =>
						value.totalDiscountValue ? (
							<PricingRow
								key={code}
								label={code}
								value={-value.totalDiscountValue}
								currency={currency}
								py={verticalLinePadding}
								variant={fontSize === 'large' ? 'body1' : 'body2'}
								textClass={textRowClass}
							/>
						) : null,
					)}
				{((!!taxExcluded && subtotal !== total) || !rows.includes(PriceRows.total)) && (
					<>
						{rows.includes(PriceRows.subtotal) && (
							<PricingRow
								label={t('common:payment.subtotal', 'Subtotal')}
								value={subtotal}
								currency={currency}
								py={verticalLinePadding}
								variant={fontSize === 'large' ? 'body1' : 'body2'}
								textClass={textRowClass}
							/>
						)}
						{rows.includes(PriceRows.taxes) &&
							taxLines.map((taxLine) => (
								<PricingRow
									key={taxLine.label?.def ?? '' + taxLine.rate}
									label={
										taxLine.label
											? getTranslation(taxLine.label, lang)
											: getDefaultTaxLabel(taxLine.rate, t)
									}
									value={taxLine.price}
									currency={currency}
									py={verticalLinePadding}
									variant={fontSize === 'large' ? 'body1' : 'body2'}
									textClass={textRowClass}
								/>
							))}
					</>
				)}
			</Box>
			{!!rows.includes(PriceRows.total) && (
				<PricingRow
					label={t('common:payment.total', 'Total')}
					value={total}
					currency={currency}
					variant={fontSize === 'large' ? 'h6' : 'body1'}
					bold={fontSize === 'large' ? false : true}
					textClass={totalRowClass}
				/>
			)}
			{!pricing.taxExcluded && !!rows.includes(PriceRows.includedTax) && !!pricing.totalTaxes && (
				<PricingRow
					label={t('common:taxes.includingTaxes', 'Including tax')}
					value={pricing.totalTaxes}
					currency={currency}
					color="secondary"
					variant={fontSize === 'large' ? 'body2' : 'caption'}
					textClass={textRowClass}
				/>
			)}
			{!!deposit && rows.includes(PriceRows.deposit) && (
				<PricingRow
					label={`+ ${t('common:payment.deposit', 'Deposit')}`}
					value={deposit}
					currency={currency}
					variant={fontSize === 'large' ? 'body2' : 'caption'}
					textClass={textRowClass}
				/>
			)}
			{!!payment?.paid && rows.includes(PriceRows.paid) && (
				<Box mt={1}>
					<PricingRow
						label={t('common:payment.paid', 'Paid')}
						value={payment.paid}
						currency={currency}
						variant={fontSize === 'large' ? 'body1' : 'body2'}
						py={verticalLinePadding}
						textClass={textRowClass}
					/>
					<PricingRow
						label={t('common:payment.toPay', 'Total to pay')}
						value={payment.unpaid}
						currency={currency}
						variant={fontSize === 'large' ? 'h6' : 'body1'}
						bold={fontSize === 'large' ? false : true}
						pt={verticalLinePadding}
						textClass={totalRowClass}
					/>
				</Box>
			)}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		subtotalWrapper: {
			paddingBottom: ({ styling }: Props) =>
				theme.spacing(styling?.totalRowSpacing ? styling.totalRowSpacing / 2 : 1),
			marginBottom: ({ styling }: Props) =>
				theme.spacing(styling?.totalRowSpacing ? styling.totalRowSpacing / 2 : 1),
			borderBottom: ({ styling }: Props) =>
				!!styling?.totalDivider ? `1px solid ${theme.palette.divider}` : undefined,
			'&:empty': {
				display: 'none',
			},
		},
	}),
);

export default PriceBreakdown;
